import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";

/**
 * TASK-028: the single source of truth for "which RFx am I evaluating?".
 *
 * Every evaluation surface used to derive this as `rfxData?.id`. That is wrong
 * for an external/guest panel member: `rfxData` comes from the procurer-detail
 * fetch, which such a member legitimately 403s on (VT-044), so `rfxData` lags
 * or never resolves at all. Anything keyed off it then builds request URLs like
 * `/rfx/undefined/evaluation/17/panel-members/25/disclaimer` — the reported
 * "everything 404s for an external panel member" bug.
 *
 * The provider's `rfxId` is seeded straight from the URL segment
 * (`useState(apiId)`) and is therefore populated on first render, independent of
 * any fetch. Prefer it always; fall back to `rfxData?.id` only for the callers
 * that render outside a `RfxDataProvider` (the context is optional there).
 *
 * @param rfxData Optional already-fetched RFx, used only as a fallback.
 * @returns The RFx id, or `null` when neither source knows it yet.
 */
export const useEvaluationRfxId = (
	rfxData?: Rfx | null
): number | null => {
	// `ignoreContextCheck` — some evaluation surfaces (e.g. EvaluationTabDisplay)
	// legitimately render without the provider and must not throw.
	const contextRfxId = useRfxData(true)?.rfxId;

	return contextRfxId ?? rfxData?.id ?? null;
};
